"use client";

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Play, X, ArrowUpRight } from 'lucide-react';
import { useState } from 'react';
import Link from 'next/link';
import ctaConfig from '@/../config/ctaConfig.json'; // Only importing CTA config

export default function HeroSection() {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <>
      <section className="relative md:min-h-screen text-white">
        {/* Inner container for background and decorations */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Background gradient - moved inside inner container */}
          <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0c0c0c] to-blue-900"></div>

          {/* Ambient lighting effects */}
          <div className="absolute top-[-100px] left-[-100px] w-[500px] h-[500px] rounded-full bg-blue-500/5 blur-[120px] pointer-events-none"></div>
          <div className="absolute bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-indigo-500/5 blur-[120px] pointer-events-none"></div>
          
          {/* Noise texture overlay */}
          <div className="absolute inset-0 opacity-[0.03] mix-blend-soft-light pointer-events-none bg-repeat">
            <div className="absolute inset-0 backdrop-filter backdrop-grayscale"></div>
          </div>
          
          {/* Decorative floating elements */}
          <div className="absolute top-[20%] left-[15%] w-8 h-8 rounded-full border border-blue-500/10 opacity-20 animate-float-slow pointer-events-none"></div>
          <div className="absolute top-[60%] right-[10%] w-12 h-12 rounded-full border border-indigo-500/10 opacity-20 animate-float-medium pointer-events-none"></div>
          <div className="absolute bottom-[10%] left-[30%] w-6 h-6 rounded-sm border border-sky-500/10 opacity-20 animate-float-fast rotate-45 pointer-events-none"></div>

          {/* Decorative elements moved inside inner container */}
          <div className="absolute left-0 top-0 -translate-x-1/4 -translate-y-1/4 z-10 opacity-20 w-48 h-48 md:w-96 md:h-96">
            {/* Decorative cube image removed */}
          </div>
          <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 z-10 opacity-20 w-48 h-48 md:w-96 md:h-96">
            <Image
              src="/images/home/cube.webp"
              alt="Decorative cube"
              width={480}
              height={480}
              className="w-full h-full"
            />
          </div>
        </div>

        {/* Main Content Container - sits above background */}
        <div className="mx-auto px-[8%] relative z-10 pt-10 pb-5">
          {/* Product Hunt Badge - Moved to top */}
          <div className="flex justify-center mb-6">
            <a
              href="https://www.producthunt.com/posts/meetminutes?utm_source=badge-featured&utm_medium=badge&utm_souce=badge-meetminutes"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src="/images/top-post-badge.svg"
                alt="MeetMinutes - AI&#0032;powered&#0032;meeting&#0032;notes&#0032;and&#0032;transcription&#0032;tool | Product Hunt"
                width={250}
                height={54}
                className="w-[250px] h-[54px]"
              />
            </a>
          </div>

          {/* Text and Buttons Container */}
          <div className="max-w-4xl mx-auto text-center relative">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 text-gray-100">
              Unlock Productivity with the Best AI Notetaker
            </h1>

            <p className="text-xl md:text-2xl text-gray-100 mb-6">
              More than just automatic record, transcribe, summarize your online and offline
              meetings with MeetMinutes
            </p>

            <div className="flex flex-col items-center sm:flex-row justify-center gap-4 mb-8">
              {/* Rainbow effect under the button */}
              <div className="relative group mx-auto sm:mx-0">
                <Link
                  href={ctaConfig.homeHero.bookDemoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button
                    className="relative bg-[#020bb8] text-white font-semibold text-md py-4 px-6 rounded-lg shadow-lg hover:shadow-xl transition-transform transform hover:scale-105 overflow-hidden flex items-center gap-2"
                  >
                    <span className="absolute inset-0 bg-[#020bb8] opacity-50 blur-md rounded-lg"></span>
                    <span className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 animate-pulse"></span>
                    <span className="relative">{ctaConfig.homeHero.bookDemoText}</span>
                    <ArrowUpRight className="h-4 w-4 relative" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Image Container (Full width within padded parent) - 130% larger container */}
          <div className="relative mt-14 w-full max-w-[1040px] mx-auto hidden md:block" id="video-section">
            <div
              className="relative rounded-xl overflow-hidden border border-gray-800 shadow-2xl aspect-video"
            >
              <Image
                src="/images/home/4quadrant.webp"
                alt="MeetMinutes Features Overview"
                width={1920}
                height={1080}
                className="rounded-xl object-cover"
              />
              {/* Video Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm py-3 px-4 rounded-full"
                  onClick={() => setShowVideo(true)}
                >
                  <Play className="mr-2 h-4 w-4" /> {ctaConfig.homeHero.watchDemoText}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal - Separate from other components */}
      {showVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999]"
          onClick={() => setShowVideo(false)}
        >
          <div
            className="relative max-w-3xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={ctaConfig.homeHero.watchDemoVideoPath}
              autoPlay
              controls
              className="w-full aspect-video rounded-lg"
            ></video>
            <button
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-70 transition-colors"
              onClick={() => setShowVideo(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style jsx global>{`
        @keyframes float-slow {
          0% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-15px) rotate(5deg); }
          100% { transform: translateY(0) rotate(0); }
        }
        @keyframes float-medium {
          0% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-10px) rotate(-5deg); }
          100% { transform: translateY(0) rotate(0); }
        }
        @keyframes float-fast {
          0% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-5px) rotate(10deg); }
          100% { transform: translateY(0) rotate(0); }
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        .animate-float-medium {
          animation: float-medium 5s ease-in-out infinite;
        }
        .animate-float-fast {
          animation: float-fast 4s ease-in-out infinite;
        }
      `}</style>
    </>
  );
} 